import { useQuery } from "@tanstack/react-query";

const COLS = "id,name,price,offer_price,image,seo_slug,rating,stock,hijab_enabled,hijab_price";
const QUERY_TIMEOUT_MS = 8000;
const REST_BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1`;
const REST_HEADERS: HeadersInit = {
  apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      globalThis.setTimeout(() => reject(new Error("design_products_timeout")), timeoutMs);
    }),
  ]);

interface DesignQueryConfig {
  designId: string;
  newLimit?: number;
  bestLimit?: number;
  allLimit?: number;
  categories?: boolean;
  catLimit?: number;
  enabled?: boolean;
}

const fetchTableRows = async (table: string, params: Record<string, string>, method: "GET" | "POST" = "GET") => {
  const url = new URL(`${REST_BASE_URL}/${table}`);
  
  if (method === "GET") {
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  }

  const response = await fetch(url.toString(), {
    method,
    headers: {
      ...REST_HEADERS,
      ...(method === "POST" ? { "Content-Type": "application/json" } : {}),
    },
    ...(method === "POST" ? { body: JSON.stringify(params) } : {}),
  });

  if (!response.ok) {
    throw new Error(`${table}_http_${response.status}`);
  }

  return response.json();
};

const fetchDesignData = async (config: DesignQueryConfig) => {
  const promises: Promise<any>[] = [];
  const keys: string[] = [];

  if (config.newLimit) {
    keys.push("newProducts");
    promises.push(
      fetchTableRows("products", {
        select: COLS,
        is_active: "eq.true",
        order: "created_at.desc",
        limit: String(config.newLimit),
      }),
    );
  }
  if (config.bestLimit) {
    keys.push("bestProducts");
    promises.push(
      (async () => {
        const idsRes = await fetchTableRows(
          "rpc/get_best_selling_product_ids",
          { _limit: String(config.bestLimit) },
          "POST",
        );
        const ids: string[] = (idsRes || []).map((row: any) => row.product_id).filter(Boolean);

        if (ids.length === 0) {
          return fetchTableRows("products", {
            select: COLS,
            is_active: "eq.true",
            order: "rating.desc,created_at.desc",
            limit: String(config.bestLimit),
          });
        }

        const products = await fetchTableRows("products", {
          select: COLS,
          is_active: "eq.true",
          id: `in.(${ids.join(",")})`,
        });

        const idOrder = new Map(ids.map((id, index) => [id, index]));
        return (products || []).sort((a: any, b: any) => (idOrder.get(a.id) ?? 9999) - (idOrder.get(b.id) ?? 9999));
      })(),
    );
  }
  if (config.allLimit) {
    keys.push("allProducts");
    promises.push(
      fetchTableRows("products", {
        select: COLS,
        is_active: "eq.true",
        order: "sort_order.asc",
        limit: String(config.allLimit),
      }),
    );
  }
  if (config.categories) {
    keys.push("categories");
    promises.push(
      fetchTableRows("categories", {
        select: "id,name,image,slug,display_size",
        show_on_home: "eq.true",
        order: "sort_order.asc",
        limit: String(config.catLimit || 6),
      }),
    );
  }

  try {
    const results = await withTimeout(Promise.all(promises), QUERY_TIMEOUT_MS);
    const data: Record<string, any[]> = {};
    keys.forEach((key, index) => {
      data[key] = results[index] || [];
    });
    return data;
  } catch (error) {
    console.error("design products load failed", error);
    return {};
  }
};

export const useDesignProducts = (config: DesignQueryConfig) => {
  const { data } = useQuery({
    queryKey: [
      "design-products",
      "hijab-v1",
      config.designId,
      config.newLimit || 0,
      config.bestLimit || 0,
      config.allLimit || 0,
      config.categories ? 1 : 0,
      config.catLimit || 0,
    ],
    queryFn: () => fetchDesignData(config),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: config.enabled !== false,
  });

  return {
    newProducts: data?.newProducts || [],
    bestProducts: data?.bestProducts || [],
    allProducts: data?.allProducts || [],
    categories: data?.categories || [],
  };
};
